import { memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled, { css } from 'styled-components'

import { updateDrawerMeta } from '@data/state/action/root'

import { Spacings, Text } from '@styles'
import { DrawerBodyWrapper, SectionHeader, H5 } from '@base'

import ServicedQuantity from './InvoiceDetails/ServicedQuantity'
import Documents from './InvoiceDetails/Documents'
import { Tabs } from '@baseV2'
import BasicDetails from './InvoiceDetails/BasicDetails'
import { isOrderEditableStatus, orderStatus, strings } from '@constants'
import { isLogisticsAdmin, isSalesAdmin, isSuperAdmin, isSuperAdminOnly } from 'src/utils/adminFilter'

export const CustomSectionHeader = styled(SectionHeader)`
	margin-top: ${Spacings.SPACING_6B};
`

export const ExportFormItemWrapper = styled.div`
	margin-top: ${Spacings.SPACING_2B};
	display: flex;
	flex-direction: row;
	flex: 1;
	justify-content: space-between;
	align-items: center;
	& > div {
		width: 100%;
	}
`

export const ChargeListWrapper = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-bottom: ${Spacings.SPACING_2B};
	& > div {
		width: 100%;
	}
	font-size: ${Text.MEDIUM};
	gap: ${Spacings.SPACING_3B};
	& > div:nth-child(2) {
		color: ${({ theme }) => theme.palette.text.blackGrey};
	}
`

export const FormElementLabel = styled.label`
	font-size: 0.75rem;
	display: block;
	margin-left: 8px;
	color: ${({ disabled, theme }) => (disabled ? theme.palette.text.greydark : theme.palette.text.black)};
	cursor: ${({ disabled }) => (disabled ? 'initial' : 'pointer')};
	user-select: none;
`

export const Headings = styled(H5)`
	padding: 0 0 ${Spacings.SPACING_3B};
	color: ${({ theme }) => theme.palette.text.darkblack};
	border-bottom: 1px solid ${({ theme }) => theme.palette.background.searchgrey};
	margin-bottom: ${Spacings.SPACING_2B};
`

export const ViewItemWrapper = styled.div`
	display: flex;
	width: 100%;
	padding: 0px 10px 5px 10px;
	margin: 10px 0;
	flex-direction: column;
	border-radius: ${Spacings.SPACING_3B};
	border: 1px solid rgba(234, 241, 250, 1);
	transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
	border-color: ${({ theme }) => theme.palette.background.disabled};

	${({ isColorChange }) => {
		if (isColorChange) {
			return css`
				background-color: ${({ theme }) => theme.palette.background.tablegrey};
			`
		}
	}}
`

const InvoiceDetailsHeader = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
	justify-content: flex-start;
`

const AdminEditInvoice = memo(({ order, update, supplierPoNumber, manageOrderType = 'enquiry', readOnly = false }) => {
	const dispatch = useDispatch()
	const invoiceData = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'invoiceData']))
	const [activeTab, setActiveTab] = useState('basic_details')
	const user = useSelector((state) => state.getIn(['user', 'user']))
	// Nothing on an order can be changed once it leaves ORDER_PLACED, so every role check below
	// is ANDed with the order's status.
	const orderEditable = isOrderEditableStatus(order)
	// Invoice fields are read-only for everyone except a super admin (a logistics admin may edit
	// Delivery Status / Date only, see deliveryOnlyEditable below).
	// ADMIN and LOGISTICS_ADMIN additionally get the Vehicle Details and POD sections, and the
	// Documents tab; SALES_ADMIN gets the Documents tab only.
	// A super admin can still correct an invoice after the order is completed / cancelled, so their
	// access to Basic Details and Serviced Quantity is not ANDed with the order's status.
	// A logistics admin works the shipment end of an order, so they get the same sections an admin
	// does here.
	const isAdminOrLogistics = isSuperAdmin(user?.role) || isLogisticsAdmin(user?.role)
	// readOnly = order history version view: every save/upload/delete below is turned off
	const canEditAll = !readOnly && isSuperAdminOnly(user?.role)
	const vehicleEditable = !readOnly && orderEditable && isAdminOrLogistics
	// POD is the one section that outlives ORDER_PLACED: the proof of delivery and the serviced /
	// rejected weights are still collected after an order is completed or short-closed, so an admin
	// keeps Request POD, Add / Edit Site Manager and Save Details there.
	const podStatusEditable =
		orderEditable || [orderStatus.completed, orderStatus.shortclosed].includes(order?.order_status)
	const podEditable = !readOnly && podStatusEditable && isAdminOrLogistics
	// A logistics admin can edit only Delivery Status and Delivery Date in Basic Details, on the
	// same statuses as POD.
	const deliveryOnlyEditable = !canEditAll && isLogisticsAdmin(user?.role) && podStatusEditable

	// Documents keep coming in after dispatch, so the tab follows the same statuses as POD —
	// only CANCELLED is left out.
	const documentsEditable = !readOnly && podStatusEditable && (isAdminOrLogistics || isSalesAdmin(user?.role, false))

	useEffect(() => {
		dispatch(
			updateDrawerMeta({
				label: (
					<InvoiceDetailsHeader>
						<Tabs
							data={[
								{
									label: strings('invoice', 'details'),
									selected: activeTab === 'basic_details',
									onClick: () => setActiveTab('basic_details'),
								},
								{
									label: strings('serviced', 'quantity'),
									selected: activeTab === 'serviced_quantity',
									onClick: () => setActiveTab('serviced_quantity'),
								},
								{
									label: strings('documents'),
									selected: activeTab === 'documents',
									onClick: () => setActiveTab('documents'),
								},
							]}
						/>
					</InvoiceDetailsHeader>
				),
			})
		)
	}, [activeTab, invoiceData])

	return (
		<DrawerBodyWrapper>
			{activeTab === 'basic_details' && (
				<BasicDetails
					supplierPoNumber={supplierPoNumber}
					update={update}
					editable={canEditAll}
					deliveryOnly={deliveryOnlyEditable}
					vehicleEditable={vehicleEditable}
					podEditable={podEditable}
				/>
			)}
			{activeTab === 'serviced_quantity' && <ServicedQuantity supplierPoNumber={supplierPoNumber} update={update} editable={canEditAll} />}
			{activeTab === 'documents' && <Documents supplierPoNumber={supplierPoNumber} update={update} editable={documentsEditable} />}
		</DrawerBodyWrapper>
	)
})

export default AdminEditInvoice
